/**
 * Boot-failure shell (roc-odn8).
 *
 * When `loadRuntimeConfig()` rejects there is no app: no Vue, no router, no
 * toast host, so `useStaleBuildNotice` — which owns every other stale-tab
 * message — never mounts. Whatever this module paints is the operator's entire
 * account of what happened.
 *
 * That matters because one of the two ways to land here is not a broken
 * deployment at all. A tab booting an old bundle against a current
 * `/config.json` fails validation on fields the two builds disagree about, and
 * the honest fix is a reload, not a page. Incident #4 (2026-08-08) was exactly
 * this: a pre-f43b053 bundle rejecting a current config for a missing
 * `kmlBaseUrl` on a stage stamp that was healthy the whole time, reported as
 * "stage is down".
 *
 * So the shell asks the server which build it serves before it commits to a
 * story. Kept dependency-free for the same reason as stale-build.ts: main.ts
 * runs it before the runtime config is installed, so it cannot pull in anything
 * that reads config at module scope.
 */
import { isRunningStaleBuild, markStaleBuild } from './stale-build';
import { isTransientModuleFetchError } from './boot-retry';

/**
 * Which half of main.ts's boot failed. They fail for unrelated reasons and the
 * operator's next move differs, so the shell is told which one rather than
 * assuming (roc-mez2.3.3): `config` is a deployment that cannot run, `app` is
 * usually a network that dropped a request this tab already retried.
 */
export type BootStage = 'config' | 'app';

const LEAD: Record<BootStage, string> = {
  config: 'The deployment configuration (<code>/config.json</code>) could not be loaded.',
  app: 'The application could not be loaded from the server.',
};

const WRAPPER_STYLE =
  'font-family: system-ui, sans-serif; max-width: 40rem; margin: 4rem auto; padding: 0 1rem;';
const DETAIL_STYLE =
  'white-space: pre-wrap; background: rgba(128,128,128,.15); padding: .75rem; border-radius: 4px;';

/**
 * Paint one of the two stories into `el`.
 *
 * `detail` is written with textContent rather than interpolated: the error
 * message may contain markup-significant characters and must render inert.
 */
function paint(el: HTMLElement, heading: string, lead: string, detail: string, reload: boolean) {
  el.innerHTML = `
    <div style="${WRAPPER_STYLE}">
      <h1 style="font-size: 1.25rem;">${heading}</h1>
      <p>${lead}</p>
      ${reload ? '<p><button type="button" data-testid="boot-failure-reload">Reload</button></p>' : ''}
      <pre style="${DETAIL_STYLE}"></pre>
    </div>`;
  el.querySelector('pre')!.textContent = detail;
  if (reload) {
    el.querySelector<HTMLButtonElement>('[data-testid="boot-failure-reload"]')!.addEventListener(
      'click',
      () => window.location.reload(),
    );
  }
}

/**
 * Render the boot failure, then correct it if the tab turns out to be stale.
 *
 * The generic message goes up first and the probe upgrades it afterwards: the
 * probe is a network round trip, and a blank page while it hangs would be worse
 * than a message that is briefly less specific than it could be.
 */
export async function renderBootFailure(
  el: HTMLElement,
  error: unknown,
  stage: BootStage,
): Promise<void> {
  const message = error instanceof Error ? error.message : String(error);
  // A module request that never arrived — after boot-retry.ts already tried
  // three times — leaves the operator on a page with nothing to do. Reload is
  // the action that fixes it, so offer it. A config the deployment cannot
  // supply is not fixed by reloading, and is not offered one.
  const retryable = stage === 'app' && isTransientModuleFetchError(error);
  paint(el, 'ROC failed to start', LEAD[stage], message, retryable);

  if ((await isRunningStaleBuild()) !== true) return;

  // Record it even though nothing is listening yet — staleBuildReason() is the
  // app's answer to "is this build current", and it should not be lying just
  // because the app that usually asks never started.
  markStaleBuild('entry-changed');
  paint(
    el,
    'ROC has been updated',
    'This tab is running an older version of ROC than the server is serving, and that ' +
      'older version does not understand the current deployment configuration. ' +
      'The deployment is fine — reload to pick up the new version.',
    message,
    true,
  );
}
